import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Page, Header, Content } from '@backstage/core-components';
import { DocsAssistantWidget } from '../DocsAssistant';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import rehypeRaw from 'rehype-raw';
import { visit } from 'unist-util-visit';
import {
  docsStructure,
  findDocById,
  findDocByUrlPath,
  docPathToUrl,
  getAncestorIds,
  HOME_ID,
  type DocItem,
} from './docsStructure';

const SIDEBAR_WIDTH = 280;


const useStyles = makeStyles()((theme) => ({
  // The theme gives Content overflow-x: hidden, which makes it a scroll
  // container that never scrolls (the window does): the sticky sidebar would
  // stick to it, stay at the top of the page and end after one screen. clip
  // cuts the same overflow without making a scroll container. Doubled to win
  // over the theme's styles.
  content: {
    '&&': { overflowX: 'clip' },
  },
  layout: {
    display: 'flex',
    minHeight: '100%',
  },

  // ── Sidebar ──────────────────────────────────────────────────────────────
  sidebar: {
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    height: '100vh',
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
    overflowX: 'hidden',
    background: theme.palette.background.default,
    padding: theme.spacing(1, 0, 4),
  },
  sectionLabel: {
    padding: theme.spacing(2, 2, 0.5),
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: theme.palette.text.disabled,
  },
  navItem: {
    padding: theme.spacing(0.5, 1.5),
    cursor: 'pointer',
    borderRadius: 6,
    margin: theme.spacing(0.25, 0.75),
    transition: 'background 120ms',
    '&:hover': { background: theme.palette.action.hover },
  },
  navItemActive: {
    background: `${theme.palette.primary.main}22`,
    '& $navItemText': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  navItemText: {
    fontSize: '0.85rem',
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  navItemCategory: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  navItemIndent1: { paddingLeft: theme.spacing(2.5) },
  navItemIndent2: { paddingLeft: theme.spacing(4) },
  navItemIndent3: { paddingLeft: theme.spacing(5.5) },
  navItemIndent4: { paddingLeft: theme.spacing(7) },
  // ── Right column ─────────────────────────────────────────────────────────
  rightColumn: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },

  // ── Content area ─────────────────────────────────────────────────────────
  contentArea: {
    padding: theme.spacing(5, 8),
    maxWidth: 860,
    '& details': {
      margin: theme.spacing(2.5, 0),
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.03)',
      padding: theme.spacing(1.5, 2.5),
    },
    '& summary': {
      fontWeight: 600,
      fontSize: '0.9rem',
      cursor: 'pointer',
      color: theme.palette.text.primary,
    },
    '& details[open] summary': {
      marginBottom: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& details table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: theme.spacing(1.5),
      fontSize: '0.875rem',
      display: 'block',
      overflowX: 'auto',
      '& th': {
        padding: theme.spacing(1.25, 2),
        borderBottom: `2px solid ${theme.palette.divider}`,
        fontWeight: 700,
        textAlign: 'left',
        fontSize: '0.72rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        background: theme.palette.background.paper,
        whiteSpace: 'nowrap',
      },
      '& td': {
        padding: theme.spacing(1.25, 2),
        verticalAlign: 'top',
        lineHeight: 1.6,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& tr:hover': { background: theme.palette.action.hover },
    },
    '& .admonition': {
      margin: theme.spacing(2.5, 0),
      borderRadius: 8,
      padding: theme.spacing(1.5, 2.5),
      borderLeft: '4px solid',
      color: theme.palette.text.primary,
      '& p:last-child': { marginBottom: 0 },
    },
    '& .admonition-title': {
      fontWeight: 700,
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginBottom: theme.spacing(1),
      marginTop: 0,
    },
    '& .admonition-tip': {
      background: theme.palette.mode === 'dark' ? 'rgba(39,174,96,0.12)' : 'rgba(39,174,96,0.08)',
      borderColor: '#27ae60',
      '& .admonition-title': { color: '#27ae60' },
    },
    '& .admonition-info, & .admonition-note': {
      background: theme.palette.mode === 'dark' ? 'rgba(52,152,219,0.12)' : 'rgba(52,152,219,0.08)',
      borderColor: '#3498db',
      '& .admonition-title': { color: '#3498db' },
    },
    '& .admonition-warning, & .admonition-caution, & .admonition-warn': {
      background: theme.palette.mode === 'dark' ? 'rgba(243,156,18,0.12)' : 'rgba(243,156,18,0.08)',
      borderColor: '#f39c12',
      '& .admonition-title': { color: '#e67e22' },
    },
    '& .admonition-danger': {
      background: theme.palette.mode === 'dark' ? 'rgba(231,76,60,0.12)' : 'rgba(231,76,60,0.08)',
      borderColor: '#e74c3c',
      '& .admonition-title': { color: '#e74c3c' },
    },
    '& ul': { listStyleType: 'disc', paddingLeft: theme.spacing(3.5) },
    '& ul ul': { listStyleType: 'circle' },
    '& ul ul ul': { listStyleType: 'square' },
    '& ol': { listStyleType: 'decimal', paddingLeft: theme.spacing(3.5) },
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(8),
  },
  errorBox: {
    padding: theme.spacing(3),
    color: theme.palette.error.main,
  },

  // ── Homepage ─────────────────────────────────────────────────────────────
  homeContent: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(0),
  },
  hero: {
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '12px 12px 0 0',
    margin: theme.spacing(3, 3, 0),
    overflow: 'hidden',
    minHeight: 240,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.25) 100%)'
      : 'linear-gradient(90deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.45) 100%)',
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(4, 5),
    gap: theme.spacing(4),
  },
  heroLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  heroTitle: {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
    lineHeight: 1.2,
    margin: 0,
  },
  heroSubtitle: {
    fontSize: '0.9375rem',
    color: theme.palette.mode === 'dark' ? '#D7D7DB' : theme.palette.text.secondary,
    lineHeight: 1.65,
    margin: 0,
    maxWidth: 480,
  },
  heroBtn: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    marginTop: theme.spacing(0.5),
    color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)'}`,
    borderRadius: 6,
    padding: theme.spacing(1, 3),
    fontSize: '0.875rem',
    fontWeight: 500,
    textDecoration: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'background 150ms, border-color 150ms',
    '&:hover': {
      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
      borderColor: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0,0,0,0.6)',
    },
  },
  heroImg: {
    width: 180,
    borderRadius: 8,
    flexShrink: 0,
    display: 'block',
  },
  quickLinks: {
    margin: theme.spacing(0, 3),
    background: theme.palette.mode === 'dark' ? '#1e2030' : theme.palette.background.paper,
    borderRadius: '0 0 12px 12px',
    padding: theme.spacing(2.5, 4),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  quickLinksTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.25),
  },
  quickLinksSubtitle: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  quickLinkTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  quickLinkTag: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    padding: theme.spacing(0.25, 1.5),
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    background: 'transparent',
    transition: 'background 120ms',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  homeFooter: {
    height: theme.spacing(5),
  },

  // ── Markdown typography ───────────────────────────────────────────────────
  mdH1: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.text.primary,
  },
  mdH2: {
    fontSize: '1.45rem',
    fontWeight: 700,
    lineHeight: 1.25,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  mdH3: {
    fontSize: '1.15rem',
    fontWeight: 600,
    lineHeight: 1.3,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  mdH4: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.primary,
  },
  mdH5: {
    fontSize: '0.8rem',
    fontWeight: 700,
    lineHeight: 1.4,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  mdH6: {
    fontSize: '0.85rem',
    fontWeight: 600,
    lineHeight: 1.4,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  mdP: {
    fontSize: '0.9375rem',
    lineHeight: 1.8,
    marginTop: 0,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  mdA: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.light,
    },
  },
  mdStrong: { fontWeight: 700, color: theme.palette.text.primary },
  mdEm: { fontStyle: 'italic' },
  mdInlineCode: {
    background:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    borderRadius: 4,
    padding: '0.15em 0.45em',
    fontSize: '0.85em',
    fontFamily: '"Fira Code", "Cascadia Code", Consolas, monospace',
    color: theme.palette.mode === 'dark' ? '#e0b1ff' : theme.palette.primary.dark,
    letterSpacing: 0,
  },
  mdPre: {
    background: theme.palette.mode === 'dark' ? '#12121f' : '#f5f5f5',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2.5, 3),
    overflowX: 'auto',
    margin: theme.spacing(2.5, 0),
    '& code': {
      background: 'none',
      padding: 0,
      fontSize: '0.85rem',
      fontFamily: '"Fira Code", "Cascadia Code", Consolas, monospace',
      lineHeight: 1.65,
      color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1a202c',
      display: 'block',
    },
  },
  mdBlockquote: {
    margin: theme.spacing(2.5, 0),
    padding: theme.spacing(1.5, 2.5),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(152,148,249,0.07)'
        : 'rgba(0,0,0,0.03)',
    borderRadius: '0 8px 8px 0',
    '& p': {
      margin: 0,
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
      fontSize: '0.9375rem',
      lineHeight: 1.7,
    },
  },
  mdUl: {
    listStyleType: 'disc',
    margin: theme.spacing(0, 0, 2),
    paddingLeft: theme.spacing(3.5),
    '& li': {
      marginBottom: theme.spacing(0.75),
      fontSize: '0.9375rem',
      lineHeight: 1.7,
      color: theme.palette.text.primary,
    },
    '& ul': { marginTop: theme.spacing(0.5), marginBottom: 0, listStyleType: 'circle' },
  },
  mdOl: {
    listStyleType: 'decimal',
    margin: theme.spacing(0, 0, 2),
    paddingLeft: theme.spacing(3.5),
    '& li': {
      marginBottom: theme.spacing(0.75),
      fontSize: '0.9375rem',
      lineHeight: 1.7,
      color: theme.palette.text.primary,
    },
    '& ol': { marginTop: theme.spacing(0.5), marginBottom: 0 },
  },
  mdTable: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: theme.spacing(2.5, 0),
    fontSize: '0.875rem',
    overflowX: 'auto',
    display: 'block',
  },
  mdThead: {
    background: theme.palette.background.paper,
    '& th': {
      padding: theme.spacing(1.25, 2),
      borderBottom: `2px solid ${theme.palette.divider}`,
      fontWeight: 700,
      textAlign: 'left',
      fontSize: '0.72rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
    },
  },
  mdTbody: {
    '& tr': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      transition: 'background 120ms',
      '&:hover': { background: theme.palette.action.hover },
    },
    '& tr:nth-child(odd)': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.015)'
          : 'rgba(0,0,0,0.015)',
    },
    '& td': {
      padding: theme.spacing(1.25, 2),
      verticalAlign: 'top',
      lineHeight: 1.6,
      color: theme.palette.text.primary,
    },
  },
  mdHr: {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(4, 0),
  },
  mdImg: {
    maxWidth: '100%',
    borderRadius: 8,
    margin: theme.spacing(1.5, 0),
    display: 'block',
  },

  // ── Tabs ──────────────────────────────────────────────────────────────────
  tabsContainer: {
    margin: theme.spacing(2.5, 0),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  tabsHeader: {
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    overflowX: 'auto',
  },
  tabBtn: {
    padding: theme.spacing(1, 2.5),
    border: 'none',
    borderBottom: '2px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    transition: 'color 120ms, border-color 120ms',
    whiteSpace: 'nowrap',
    '&:hover': { color: theme.palette.text.primary },
  },
  tabBtnActive: {
    color: theme.palette.primary.main,
    borderBottomColor: theme.palette.primary.main,
    fontWeight: 600,
  },
  tabContent: {
    padding: theme.spacing(2.5, 3),
  },
}));

// ── Tab types ────────────────────────────────────────────────────────────

interface ParsedTab {
  value: string;
  label: string;
  content: string;
}

interface ParsedTabs {
  defaultValue: string;
  tabs: ParsedTab[];
}

type Segment =
  | { type: 'md'; content: string }
  | { type: 'tabs'; data: ParsedTabs }
  | { type: 'admonition'; admType: string; label: string; children: Segment[] };

// ── Pipe-table → HTML converter (used inside <details> blocks) ───────────

function parseTableRow(line: string): string[] {
  // Strip leading/trailing whitespace and optional leading/trailing pipe
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map(c => c.trim());
}

function isSeparatorLine(line: string): boolean {
  // e.g. "--- | --- | ---" or "| :--- | --- |"
  return /^[\s|:\-]+$/.test(line) && line.includes('-') && line.includes('|');
}

function pipeTableToHtml(tableText: string): string {
  const lines = tableText
    .split('\n')
    .map(l => l.replace(/^[ \t]+/, '')) // dedent
    .filter(Boolean);

  const sepIdx = lines.findIndex(isSeparatorLine);
  if (sepIdx < 1) return tableText;

  const headers = parseTableRow(lines[0]);
  const bodyRows = lines.slice(sepIdx + 1).filter(l => l.includes('|'));

  const ths = headers.map(h => `<th>${h}</th>`).join('');
  const trs = bodyRows
    .map(row => `<tr>${parseTableRow(row).map(c => `<td>${c}</td>`).join('')}</tr>`)
    .join('');
  return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
}

function processDetailsBlocks(content: string, docPath: string): string {
  const docDir = docPath.split('/').slice(0, -1).join('/');

  // Within each <details> block, convert pipe-table syntax and markdown links
  // to HTML — the markdown parser won't process these inside HTML blocks.
  return content.replace(
    /(<details[^>]*>[\s\S]*?<\/summary>)([\s\S]*?)(<\/details>)/g,
    (_, head, body, close) => {
      // 1. Convert pipe tables
      let converted = body.replace(
        /((?:[ \t]*[^\n]*\|[^\n]*\n){2,})/g,
        (tableMatch: string) => {
          if (!tableMatch.split('\n').some(isSeparatorLine)) return tableMatch;
          return '\n' + pipeTableToHtml(tableMatch) + '\n';
        },
      );

      // 2. Convert markdown links [text](href) to <a> tags
      converted = converted.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (_: string, text: string, href: string) => {
          if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
          }
          // Same-page anchors and links to other docs pages (no file
          // extension) are routed by the `a` component like any other link.
          if (href.startsWith('#') || !/\.[A-Za-z0-9]+$/.test(href.split('#')[0])) {
            return `<a href="${href}">${text}</a>`;
          }
          // Relative file link (e.g. .json, .pdf) — resolve to /docs-content/
          const resolved = href.startsWith('/') ? href : (docDir ? `${docDir}/${href}` : href);
          return `<a href="/docs-content/${resolved}" class="docs-file-dl">${text}</a>`;
        },
      );

      return head + converted + close;
    },
  );
}

// ── Raw-loader import extractor ───────────────────────────────────────────
// Returns { varName: resolvedFilePath } for all !!raw-loader! imports.
function extractRawLoaderVars(raw: string, docDir: string): Record<string, string> {
  const map: Record<string, string> = {};
  const re = /^import\s+(\w+)\s+from\s+["']!!raw-loader!([^"']+)["']/gm;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(raw)) !== null) {
    let filePath = m[2];
    if (filePath.startsWith('./')) filePath = filePath.slice(2);
    map[m[1]] = docDir ? `${docDir}/${filePath}` : filePath;
  }
  return map;
}

// ── MDX preprocessor ─────────────────────────────────────────────────────

// Removes top-level `export const X = ...` / `export default ...` JSX component
// definitions (Docusaurus MDX). They are not content and would otherwise render
// as raw text at the top of the page. Multi-line definitions are handled by
// tracking bracket depth until the block closes.
function stripExportBlocks(raw: string): string {
  const lines = raw.split('\n');
  const out: string[] = [];
  let removing = false;
  let depth = 0;
  for (const line of lines) {
    if (!removing && /^export\b/.test(line)) {
      removing = true;
      depth = 0;
    }
    if (removing) {
      for (const ch of line) {
        if (ch === '(' || ch === '{' || ch === '[') depth++;
        else if (ch === ')' || ch === '}' || ch === ']') depth--;
      }
      if (depth <= 0) removing = false;
      continue; // drop this line
    }
    out.push(line);
  }
  return out.join('\n');
}

function preprocessContent(raw: string, docPath = '', varContents: Record<string, string> = {}): string {
  let c = raw;
  // Extract title from frontmatter before stripping it
  const titleMatch = c.match(/^---[\s\S]*?^title:\s*['"]?(.+?)['"]?\s*$/m);
  const frontmatterTitle = titleMatch?.[1]?.trim() ?? null;
  // Strip frontmatter
  c = c.replace(/^---[\s\S]*?---\s*\n?/, '');
  // Strip MDX/JSX comments {/* ... */}
  c = c.replace(/\{\/\*[\s\S]*?\*\/\}/g, '');
  // Strip mdx-code-block fence markers but keep the content inside
  // (Docusaurus renders the content as MDX, not as literal code)
  c = c.replace(/^```mdx-code-block\n([\s\S]*?)^```/gm, '$1');
  // Strip import statements
  c = c.replace(/^import\s+[\s\S]*?from\s+['"][^'"]+['"];?\s*\n?/gm, '');
  // Strip export component definitions (e.g. export const Highlight = ...)
  c = stripExportBlocks(c);
  // Inline raw-loader variable content: {VarName} → fetched file content
  if (Object.keys(varContents).length > 0) {
    c = c.replace(/\{(\w+)\}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(varContents, name) ? varContents[name] : match,
    );
  }
  // Convert <CodeBlock language="lang" ...>content</CodeBlock> to fenced code blocks
  c = c.replace(
    /<CodeBlock\s+language="([^"]+)"[^>]*>([\s\S]*?)<\/CodeBlock>/g,
    (_m, lang, code) => `\`\`\`${lang}\n${code.trim()}\n\`\`\``,
  );
  // Strip diff-highlight markers (used in MONAI examples for visual emphasis)
  c = c.replace(/^[ \t]*#\s*diff-highlight(?:-start|-end)?\s*$/gm, '');
  // Rewrite Docusaurus @site/static/img/ paths to the served public path
  c = c.replace(/@site\/static\/img\//g, '/docs-content/img/');
  // Rewrite JSX require("...").default image expressions to plain src strings
  c = c.replace(/\{require\(["']([^"']+)["']\)\.default\}/g, '$1');
  // Strip uppercase React component tags — but KEEP <Tabs> and <TabItem>
  // so parseTabsBlocks can find and render them natively.
  c = c.replace(/<(?!Tabs\b|TabItem\b)[A-Z][A-Za-z]*[^>]*\/>/g, '');
  c = c.replace(/<\/(?!Tabs\b|TabItem\b)[A-Z][A-Za-z]*>/g, '');
  c = c.replace(/<(?!Tabs\b|TabItem\b)[A-Z][A-Za-z]*(\s[^>]*)?\s*>/g, '');
  // Strip stray <div> / </div> wrappers left over from JSX layout blocks
  c = c.replace(/^<div[^>]*>\s*$/gm, '');
  c = c.replace(/^<\/div>\s*$/gm, '');
  // Convert pipe tables inside <details> blocks to HTML (remark won't do it)
  c = processDetailsBlocks(c, docPath);
  // Normalize Docusaurus admonition titles: :::type Title → :::type[Title]
  // remark-directive requires brackets around the label.
  // Only match a title on the SAME line (\h = spaces/tabs, not newlines) — a
  // greedy \s+ would swallow the blank line and pull the first content line
  // (and any links inside it) into the label, dropping it from the body.
  c = c.replace(/^:::(tip|info|note|warning|caution|danger|warn)[ \t]+(.+)$/gm, ':::$1[$2]');
  // Strip lone heading markers with no content (e.g. a bare `#` on its own line)
  c = c.replace(/^#{1,6}\s*$/gm, '');
  // Collapse excess blank lines
  c = c.replace(/\n{3,}/g, '\n\n');
  c = c.trim();
  // Prepend frontmatter title if the content has no h1 of its own
  if (frontmatterTitle && !c.startsWith('# ')) {
    c = `# ${frontmatterTitle}\n\n${c}`;
  }
  return c;
}

// ── Tab block parser ──────────────────────────────────────────────────────

// Finds the closing > of an opening JSX tag, handling quoted strings and
// nested {..} attribute blocks (e.g. values={[{...}, ...]} or attributes={{...}}).
function findOpenTagEnd(content: string, start: number): number {
  let depth = 0;
  let inString = false;
  let stringChar = '';
  for (let i = start; i < content.length; i++) {
    const ch = content[i];
    if (inString) {
      if (ch === stringChar && content[i - 1] !== '\\') inString = false;
      continue;
    }
    if (ch === '"' || ch === "'") { inString = true; stringChar = ch; continue; }
    if (ch === '{') { depth++; continue; }
    if (ch === '}') { depth--; continue; }
    if (ch === '>' && depth === 0) return i;
  }
  return -1;
}

// Finds the matching closing tag for an already-opened tag, accounting for
// nested opens of the same kind (e.g. <Tabs> inside <Tabs>). `from` is the
// index just past the opening tag; returns the index of the matching close, or
// -1. Relies on `openNeedle` not being a substring of `closeNeedle`
// (e.g. "<Tabs" never matches inside "</Tabs>").
function findMatchingClose(content: string, from: number, openNeedle: string, closeNeedle: string): number {
  let depth = 1;
  let i = from;
  while (i < content.length) {
    const nextOpen = content.indexOf(openNeedle, i);
    const nextClose = content.indexOf(closeNeedle, i);
    if (nextClose === -1) return -1;
    if (nextOpen !== -1 && nextOpen < nextClose) {
      depth++;
      i = nextOpen + openNeedle.length;
    } else {
      depth--;
      if (depth === 0) return nextClose;
      i = nextClose + closeNeedle.length;
    }
  }
  return -1;
}

// Parses the values={[{ label: '...', value: '...' }, ...]} attribute of a
// <Tabs> opening tag into an array of { value, label } pairs.
function parseTabsValues(openTag: string): Array<{ value: string; label: string }> {
  // Allow whitespace/newlines between the closing ] and } — Docusaurus tab
  // definitions often put the closing brace on its own line.
  const valuesMatch = openTag.match(/values=\{(\[[\s\S]*?\])\s*\}/);
  if (!valuesMatch) return [];
  const arrayStr = valuesMatch[1];
  const items: Array<{ value: string; label: string }> = [];
  // label before value
  const re1 = /\{[^}]*label:\s*['"]([^'"]+)['"][^}]*value:\s*['"]([^'"]+)['"][^}]*\}/g;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re1.exec(arrayStr)) !== null) items.push({ label: m[1], value: m[2] });
  if (items.length === 0) {
    // value before label
    const re2 = /\{[^}]*value:\s*['"]([^'"]+)['"][^}]*label:\s*['"]([^'"]+)['"][^}]*\}/g;
    // eslint-disable-next-line no-cond-assign
    while ((m = re2.exec(arrayStr)) !== null) items.push({ value: m[1], label: m[2] });
  }
  return items;
}

// Extracts <TabItem> children from a <Tabs> inner block.
function parseTabItems(inner: string, valuesMap: Record<string, string>): ParsedTab[] {
  const tabs: ParsedTab[] = [];
  let pos = 0;
  while (pos < inner.length) {
    const start = inner.indexOf('<TabItem', pos);
    if (start === -1) break;
    const openEnd = findOpenTagEnd(inner, start);
    if (openEnd === -1) break;
    const openTag = inner.slice(start, openEnd + 1);
    const valueMatch = openTag.match(/value="([^"]+)"/);
    const labelMatch = openTag.match(/label="([^"]+)"/);
    const value = valueMatch?.[1] ?? '';
    const label = labelMatch?.[1] ?? valuesMap[value] ?? value;
    const closeStr = '</TabItem>';
    const closePos = findMatchingClose(inner, openEnd + 1, '<TabItem', closeStr);
    if (closePos === -1) break;
    const tabContent = inner.slice(openEnd + 1, closePos).trim();
    if (value) tabs.push({ value, label, content: tabContent });
    pos = closePos + closeStr.length;
  }
  return tabs;
}

// Splits pre-processed MDX content into alternating markdown segments and
// parsed tab blocks.  Markdown segments go directly to ReactMarkdown;
// tab blocks are rendered by TabsRenderer.
function parseTabsBlocks(content: string): Segment[] {
  const segments: Segment[] = [];
  let pos = 0;
  while (pos < content.length) {
    const tabsStart = content.indexOf('<Tabs', pos);
    if (tabsStart === -1) {
      const rest = content.slice(pos);
      if (rest.trim()) segments.push({ type: 'md', content: rest });
      break;
    }
    const before = content.slice(pos, tabsStart);
    if (before.trim()) segments.push({ type: 'md', content: before });

    const openEnd = findOpenTagEnd(content, tabsStart);
    if (openEnd === -1) { segments.push({ type: 'md', content: content.slice(pos) }); break; }

    const openTag = content.slice(tabsStart, openEnd + 1);
    const defaultMatch = openTag.match(/defaultValue="([^"]+)"/);
    const defaultValue = defaultMatch?.[1] ?? '';
    const valuesFromTag = parseTabsValues(openTag);
    const valuesMap: Record<string, string> = {};
    valuesFromTag.forEach(v => { valuesMap[v.value] = v.label; });

    const closeStr = '</Tabs>';
    const tabsEnd = findMatchingClose(content, openEnd + 1, '<Tabs', closeStr);
    if (tabsEnd === -1) { segments.push({ type: 'md', content: content.slice(pos) }); break; }

    const inner = content.slice(openEnd + 1, tabsEnd);
    const tabs = parseTabItems(inner, valuesMap);
    if (tabs.length > 0) {
      segments.push({
        type: 'tabs',
        data: { defaultValue: defaultValue || tabs[0].value, tabs },
      });
    }
    pos = tabsEnd + closeStr.length;
  }
  return segments;
}

// Splits content into segments, additionally extracting admonition blocks that
// wrap a <Tabs> component. remark-directive cannot parse such admonitions
// (the tab block is pulled out into its own segment first), which would leave
// the bare ::: markers rendering as literal text. We render those admonitions
// ourselves as a styled box whose children are parsed recursively.
const ADMONITION_BLOCK_RE =
  /^:::(tip|info|note|warning|caution|danger|warn)(?:\[([^\]]*)\])?[ \t]*\r?\n([\s\S]*?)^:::[ \t]*$/gm;

function parseSegments(content: string): Segment[] {
  const segments: Segment[] = [];
  const re = new RegExp(ADMONITION_BLOCK_RE.source, 'gm');
  let last = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(content)) !== null) {
    // Only special-case admonitions that contain tabs; plain admonitions are
    // handled correctly by remark-directive in the md path.
    if (!m[3].includes('<Tabs')) continue;
    // Skip admonitions nested inside an unclosed <Tabs> — those are handled by
    // the tab renderer, and extracting them here would break the outer tabs.
    const pre = content.slice(0, m.index);
    const opens = (pre.match(/<Tabs/g) ?? []).length;
    const closes = (pre.match(/<\/Tabs>/g) ?? []).length;
    if (opens > closes) continue;
    const before = content.slice(last, m.index);
    parseTabsBlocks(before).forEach(s => segments.push(s));
    segments.push({
      type: 'admonition',
      admType: m[1],
      label: m[2] ?? '',
      children: parseSegments(m[3]),
    });
    last = re.lastIndex;
  }
  parseTabsBlocks(content.slice(last)).forEach(s => segments.push(s));
  return segments;
}

// ── Heading ID plugins ───────────────────────────────────────────────────

// Extracts plain text from MDAST nodes (handles text, inlineCode, nesting).
function extractMdastText(nodes: any[]): string {
  return (nodes ?? []).map(node => {
    if (node.type === 'text') return node.value ?? '';
    if (node.type === 'inlineCode') return node.value ?? '';
    if (node.type === 'html') return '';
    if (node.children) return extractMdastText(node.children);
    return '';
  }).join('');
}

// github-slugger compatible ID generation (matches medusa/Docusaurus output).
function githubSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// Sets heading ids using the same algorithm as medusa so that cross-page
// links generated by Docusaurus resolve correctly.
function remarkHeadingIds() {
  return (tree: any) => {
    visit(tree, 'heading', (node: any) => {
      const text = extractMdastText(node.children);
      if (!text.trim()) return;
      const id = githubSlug(text);
      node.data = node.data ?? {};
      node.data.hProperties = { ...(node.data.hProperties ?? {}), id };
    });
  };
}

// ── Named anchor remark plugin ───────────────────────────────────────────
// Transfers <a name="reference-xxx"></a> blocks that precede a heading into
// that heading's hProperties.id, so same-page and cross-page #fragment
// navigation targets the heading element itself (not a floating span).
function remarkNamedAnchors() {
  return (tree: any) => {
    const children: any[] = tree.children ?? [];
    const toRemove: number[] = [];
    for (let i = 0; i < children.length; i++) {
      const node = children[i];
      if (node.type !== 'html') continue;
      const match = node.value.trim().match(/^<a\s+name="([^"]+)"[^>]*><\/a>$/);
      if (!match) continue;
      const anchorId = match[1];
      const next = children[i + 1];
      if (next && next.type === 'heading') {
        next.data = next.data ?? {};
        next.data.hProperties = { ...(next.data.hProperties ?? {}), id: anchorId };
        toRemove.push(i);
      }
    }
    for (const i of toRemove.sort((a, b) => b - a)) {
      children.splice(i, 1);
    }
  };
}

// ── Admonition remark plugin ─────────────────────────────────────────────

const ADMONITION_TYPES = ['tip', 'info', 'note', 'warning', 'caution', 'danger', 'warn'] as const;

// Serialize an inline MDAST node to an HTML string.
// ReactMarkdown v8 loses link text children when links are inside
// remark-directive container content, so we serialize paragraphs to raw HTML
// and let rehype-raw parse them — that path preserves children correctly.
function inlineToHtml(node: any): string {
  switch (node.type) {
    case 'text': return node.value ?? '';
    case 'inlineCode': return `<code>${node.value ?? ''}</code>`;
    case 'html': return node.value ?? '';
    case 'break': return '<br/>';
    case 'image': return `<img src="${node.url ?? ''}" alt="${node.alt ?? ''}" />`;
    case 'strong': return `<strong>${(node.children ?? []).map(inlineToHtml).join('')}</strong>`;
    case 'emphasis': return `<em>${(node.children ?? []).map(inlineToHtml).join('')}</em>`;
    case 'link': {
      const text = (node.children ?? []).map(inlineToHtml).join('');
      const url = node.url ?? '#';
      const ext = url.startsWith('http://') || url.startsWith('https://');
      return ext
        ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`
        : `<a href="${url}">${text}</a>`;
    }
    default: return '';
  }
}

function listToHtml(node: any): string {
  const tag = node.ordered ? 'ol' : 'ul';
  const items = (node.children ?? []).map((item: any) => {
    const content = (item.children ?? []).map((child: any) => {
      if (child.type === 'paragraph') return (child.children ?? []).map(inlineToHtml).join('');
      if (child.type === 'list') return listToHtml(child);
      return '';
    }).join('');
    return `<li>${content}</li>`;
  }).join('');
  return `<${tag}>${items}</${tag}>`;
}

const ADMONITION_COLORS: Record<string, string> = {
  tip: '#27ae60',
  info: '#3498db',
  note: '#3498db',
  warning: '#f39c12',
  caution: '#f39c12',
  warn: '#f39c12',
  danger: '#e74c3c',
};

function remarkAdmonitions() {
  return (tree: any) => {
    visit(tree, 'containerDirective', (node: any, index: number | null, parent: any) => {
      if (!(ADMONITION_TYPES as readonly string[]).includes(node.name)) return;
      if (index === null || !parent) return;
      const type = node.name as string;
      const color = ADMONITION_COLORS[type] ?? '#888';

      // The title is always the type name (Tip, Warning, etc.).
      // If an explicit [label] is present and differs from the type name, it is
      // rendered as a bold leading paragraph inside the content area so no text is lost.
      const title = type.charAt(0).toUpperCase() + type.slice(1);

      const labelNode = node.children.find((c: any) => c.data?.directiveLabel);
      const labelText = labelNode
        ? labelNode.children.map((c: any) => c.value ?? '').join('').trim()
        : '';
      const contentChildren = node.children.filter((c: any) => !c.data?.directiveLabel);

      // Serialize the entire admonition to a raw HTML string and replace the
      // directive node. This lets rehype-raw parse it as a proper HTML fragment,
      // which preserves link children that ReactMarkdown v8 otherwise drops when
      // links appear inside remark-directive container content.
      // Inline styles on the title guarantee correct rendering regardless of
      // CSS class resolution order (scoped JSS rules may not always cascade into
      // deeply nested tab content).
      const labelHtml =
        labelText && labelText.toLowerCase() !== type
          ? `<p><strong>${labelText}</strong></p>`
          : '';
      const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const contentHtml = contentChildren.map((child: any) => {
        if (child.type === 'paragraph') {
          return `<p>${(child.children ?? []).map(inlineToHtml).join('')}</p>`;
        }
        if (child.type === 'list') return listToHtml(child);
        // Fenced code blocks, raw HTML and blockquotes can all appear inside an
        // admonition — render them instead of dropping the content.
        if (child.type === 'code') return `<pre><code>${escapeHtml(child.value ?? '')}</code></pre>`;
        if (child.type === 'html') return child.value ?? '';
        if (child.type === 'blockquote') {
          const inner = (child.children ?? [])
            .map((g: any) => (g.type === 'paragraph' ? `<p>${(g.children ?? []).map(inlineToHtml).join('')}</p>` : ''))
            .join('');
          return `<blockquote>${inner}</blockquote>`;
        }
        return '';
      }).join('');

      const titleStyle = `font-weight:700;font-size:0.75rem;text-transform:uppercase;letter-spacing:0.08em;margin:0 0 6px 0;color:${color}`;
      const fullHtml = `<div class="admonition admonition-${type}"><p class="admonition-title" style="${titleStyle}">${title}</p>${labelHtml}${contentHtml}</div>`;
      parent.children[index] = { type: 'html', value: fullHtml };
    });
  };
}

// ── Markdown component map ────────────────────────────────────────────────

function resolveInternalDocPath(href: string, currentDocPath: string): string {
  const [withQuery] = href.split('#');
  const [pathPart] = withQuery.split('?');

  let rawPath: string;
  if (pathPart.startsWith('/')) {
    rawPath = pathPart.slice(1);
  } else {
    const currentDir = currentDocPath.split('/').slice(0, -1).join('/');
    rawPath = currentDir ? `${currentDir}/${pathPart}` : pathPart;
  }

  // Resolve .. and . segments
  const parts = rawPath.split('/');
  const resolved: string[] = [];
  for (const part of parts) {
    if (part === '..') resolved.pop();
    else if (part !== '.') resolved.push(part);
  }

  return resolved.join('/').replace(/\.(mdx?)$/, '');
}

function extractReactText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractReactText).join('');
  if (React.isValidElement(node)) return extractReactText((node.props as any).children);
  return '';
}

function headingId(children: ReactNode): string {
  const text = extractReactText(children);
  return text.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
}

function makeComponents(
  classes: ReturnType<typeof useStyles>['classes'],
  navigateByPath: (urlPath: string, hash?: string) => void,
  currentDocPath: string,
) {
  return {
    h1: ({ id, children }: { id?: string; children?: ReactNode }) => (
      <Typography component="h1" id={id ?? headingId(children)} className={classes.mdH1}>{children}</Typography>
    ),
    h2: ({ id, children }: { id?: string; children?: ReactNode }) => (
      <Typography component="h2" id={id ?? headingId(children)} className={classes.mdH2}>{children}</Typography>
    ),
    h3: ({ id, children }: { id?: string; children?: ReactNode }) => (
      <Typography component="h3" id={id ?? headingId(children)} className={classes.mdH3}>{children}</Typography>
    ),
    h4: ({ id, children }: { id?: string; children?: ReactNode }) => (
      <Typography component="h4" id={id ?? headingId(children)} className={classes.mdH4}>{children}</Typography>
    ),
    h5: ({ id, children }: { id?: string; children?: ReactNode }) => (
      <Typography component="h5" id={id ?? headingId(children)} className={classes.mdH5}>{children}</Typography>
    ),
    h6: ({ id, children }: { id?: string; children?: ReactNode }) => (
      <Typography component="h6" id={id ?? headingId(children)} className={classes.mdH6}>{children}</Typography>
    ),
    p: ({ className, children }: { className?: string | string[]; children?: ReactNode }) => {
      // Raw HTML <p class="admonition-title"> comes through with a className — preserve it
      const cn = Array.isArray(className) ? className.join(' ') : className;
      if (cn) return <p className={cn}>{children}</p>;
      return <Typography component="p" className={classes.mdP}>{children}</Typography>;
    },
    div: ({ className, children }: { className?: string | string[]; children?: ReactNode }) => {
      // rehype-raw stores multi-class attributes as arrays; join to space-separated string
      const cn = Array.isArray(className) ? className.join(' ') : className;
      return <div className={cn}>{children}</div>;
    },
    a: ({ href, name, children, node }: { href?: string; name?: string; children?: ReactNode; node?: any }) => {
      // ReactMarkdown v8 sometimes passes empty children for links inside remark-directive
      // container content. Fall back to extracting text directly from the HAST node.
      const resolvedChildren = (children == null || (Array.isArray(children) && children.length === 0))
        ? (node?.children ?? []).map((c: any) => c.value ?? '').join('') || children
        : children;
      // Named anchors (<a name="..."/>) used inline (e.g. inside heading text).
      // Render as <span id> so getElementById finds them reliably.
      if (!href) return name ? <span id={name}>{resolvedChildren}</span> : <>{resolvedChildren}</>;
      // Rewrite old Docusaurus site links to internal SPA routes
      if (href.startsWith('https://docs.one.deepc.pro/')) {
        const internalPath = href.replace('https://docs.one.deepc.pro/', '');
        const hi = internalPath.indexOf('#');
        const pathPart = hi !== -1 ? internalPath.slice(0, hi) : internalPath;
        const hashPart = hi !== -1 ? internalPath.slice(hi) : '';
        return (
          <a
            href={`/docs-v2/${internalPath}`}
            className={classes.mdA}
            onClick={e => { e.preventDefault(); navigateByPath(pathPart, hashPart); }}
          >
            {resolvedChildren}
          </a>
        );
      }
      if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className={classes.mdA}>
            {resolvedChildren}
          </a>
        );
      }
      // Same-page anchor — let the browser scroll natively to the named/id target.
      if (href.startsWith('#')) {
        return <a href={href} className={classes.mdA}>{resolvedChildren}</a>;
      }
      // File served from /docs-content/ (e.g. injected by processDetailsBlocks) —
      // fetch as blob to bypass the SPA router intercepting the navigation.
      if (href.startsWith('/docs-content/')) {
        const filename = href.split('/').pop() ?? 'download';
        return (
          <a
            href={href}
            className={classes.mdA}
            onClick={e => {
              e.preventDefault();
              fetch(href)
                .then(r => r.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                })
                .catch(() => window.open(href, '_blank'));
            }}
          >
            {resolvedChildren}
          </a>
        );
      }
      const docPath = resolveInternalDocPath(href, currentDocPath);
      const hi = href.indexOf('#');
      const hash = hi !== -1 ? href.slice(hi) : '';
      return (
        <a
          href={`/docs-v2/${docPath}${hash}`}
          className={classes.mdA}
          onClick={e => { e.preventDefault(); navigateByPath(docPath, hash); }}
        >
          {resolvedChildren}
        </a>
      );
    },
    strong: ({ children }: { children?: ReactNode }) => (
      <strong className={classes.mdStrong}>{children}</strong>
    ),
    em: ({ children }: { children?: ReactNode }) => (
      <em className={classes.mdEm}>{children}</em>
    ),
    code: ({ inline, className, children }: { inline?: boolean; className?: string; children?: ReactNode }) => {
      if (!inline && className === 'language-mermaid') {
        const diagram = String(children).trim();
        const encoded = btoa(unescape(encodeURIComponent(diagram)));
        return (
          <img
            src={`https://mermaid.ink/img/${encoded}`}
            alt="Diagram"
            style={{ maxWidth: '100%', margin: '16px 0', display: 'block', borderRadius: 8 }}
          />
        );
      }
      return inline ? (
        <code className={classes.mdInlineCode}>{children}</code>
      ) : (
        <code>{children}</code>
      );
    },
    pre: ({ children }: { children?: ReactNode }) => (
      <pre className={classes.mdPre}>{children}</pre>
    ),
    blockquote: ({ children }: { children?: ReactNode }) => (
      <blockquote className={classes.mdBlockquote}>{children}</blockquote>
    ),
    ul: ({ children }: { children?: ReactNode }) => (
      <ul className={classes.mdUl}>{children}</ul>
    ),
    ol: ({ children }: { children?: ReactNode }) => (
      <ol className={classes.mdOl}>{children}</ol>
    ),
    table: ({ children }: { children?: ReactNode }) => (
      <table className={classes.mdTable}>{children}</table>
    ),
    thead: ({ children }: { children?: ReactNode }) => (
      <thead className={classes.mdThead}>{children}</thead>
    ),
    tbody: ({ children }: { children?: ReactNode }) => (
      <tbody className={classes.mdTbody}>{children}</tbody>
    ),
    hr: () => <hr className={classes.mdHr} />,
    img: ({ src, alt }: { src?: string; alt?: string }) => (
      <img src={src} alt={alt ?? ''} className={classes.mdImg} />
    ),
  };
}

// ── Tabs renderer ─────────────────────────────────────────────────────────

function TabsRenderer({
  data,
  components,
}: {
  data: ParsedTabs;
  components: ReturnType<typeof makeComponents>;
}) {
  const { classes } = useStyles();
  const [active, setActive] = useState(data.defaultValue || data.tabs[0]?.value || '');
  const activeTab = data.tabs.find(t => t.value === active) ?? data.tabs[0];

  return (
    <Box className={classes.tabsContainer}>
      <Box className={classes.tabsHeader}>
        {data.tabs.map(tab => (
          <button
            key={tab.value}
            className={`${classes.tabBtn} ${tab.value === active ? classes.tabBtnActive : ''}`}
            onClick={() => setActive(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </Box>
      {activeTab && (
        <Box className={classes.tabContent}>
          {renderSegments(parseSegments(activeTab.content), components)}
        </Box>
      )}
    </Box>
  );
}

// Renders an admonition (tip/warning/…) that wraps a <Tabs> block. Its children
// are themselves segments (markdown + tab blocks) rendered recursively.
function AdmonitionBlock({
  admType,
  label,
  items,
  components,
}: {
  admType: string;
  label: string;
  items: Segment[];
  components: ReturnType<typeof makeComponents>;
}) {
  const color = ADMONITION_COLORS[admType] ?? '#888';
  const title = admType.charAt(0).toUpperCase() + admType.slice(1);
  return (
    <div className={`admonition admonition-${admType}`}>
      <p
        className="admonition-title"
        style={{
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          margin: '0 0 6px 0',
          color,
        }}
      >
        {title}
      </p>
      {label && label.toLowerCase() !== admType && (
        <p><strong>{label}</strong></p>
      )}
      {renderSegments(items, components)}
    </div>
  );
}

// Renders a list of segments: markdown via ReactMarkdown, tab blocks via
// TabsRenderer, and tab-wrapping admonitions via AdmonitionBlock.
function renderSegments(
  segments: Segment[],
  components: ReturnType<typeof makeComponents>,
): ReactNode[] {
  return segments.map((seg, i) => {
    if (seg.type === 'md') {
      return (
        <ReactMarkdown
          key={i}
          remarkPlugins={[remarkGfm, remarkDirective, remarkHeadingIds, remarkNamedAnchors, remarkAdmonitions]}
          rehypePlugins={[rehypeRaw]}
          components={components as any}
        >
          {seg.content}
        </ReactMarkdown>
      );
    }
    if (seg.type === 'tabs') {
      return <TabsRenderer key={i} data={seg.data} components={components} />;
    }
    return (
      <AdmonitionBlock
        key={i}
        admType={seg.admType}
        label={seg.label}
        items={seg.children}
        components={components}
      />
    );
  });
}

// ── Homepage ──────────────────────────────────────────────────────────────

interface QuickLink {
  label: string;
  docId: string;
}

const engineQuickLinks: QuickLink[] = [
  { label: 'Basic requirements', docId: 'sdk-compliance' },
  { label: 'Build your AI model', docId: 'wt-handson' },
  { label: 'MONAI compatibility', docId: 'sdk-support-monai' },
  { label: 'Changelog', docId: 'engines-changelog' },
  { label: 'Examples', docId: 'specs-ex-report' },
];

const clinicalQuickLinks: QuickLink[] = [
  { label: 'Overview', docId: 'clinical-overview' },
  { label: 'Webhooks API', docId: 'clinical-webhooks-api' },
  { label: 'GraphQL API', docId: 'clinical-graphql-api' },
  { label: 'FHIR API', docId: 'clinical-fhir-api' },
  { label: 'AIR Integration', docId: 'clinical-air-api' },
];

function DocsHomePage({ onNavigate }: { onNavigate: (id: string) => void }) {
  const { classes } = useStyles();

  return (
    <Box>
      {/* Hero 1 – AI Developers */}
      <Box
        className={classes.hero}
        style={{ backgroundImage: "url('/docs-content/img/landing/DD_AI_vendor_bg.png')" }}
      >
        <Box className={classes.heroOverlay} />
        <Box className={classes.heroInner}>
          <Box className={classes.heroLeft}>
            <Typography component="h1" className={classes.heroTitle}>
              For AI developers of AI Products
            </Typography>
            <Typography component="p" className={classes.heroSubtitle}>
              Easily integrate your AI models, both commercial and research, into the
              deepcOS ecosystem. Package, submit and optimize your AI models for smooth
              integration with existing radiology workflows.
            </Typography>
            <button
              className={classes.heroBtn}
              onClick={() => onNavigate('avi-integration-summary')}
            >
              Go to AI Model SDK →
            </button>
          </Box>
          <img
            src="/docs-content/img/landing/DD_square.png"
            alt="deepc AI model"
            className={classes.heroImg}
          />
        </Box>
      </Box>
      <Box className={classes.quickLinks}>
        <Typography className={classes.quickLinksTitle}>Quick Links</Typography>
        <Typography className={classes.quickLinksSubtitle}>
          Jump-start your AI application with these helpful shortcuts
        </Typography>
        <Box className={classes.quickLinkTags}>
          {engineQuickLinks.map(link => (
            <button
              key={link.docId}
              className={classes.quickLinkTag}
              onClick={() => onNavigate(link.docId)}
            >
              {link.label}
            </button>
          ))}
        </Box>
      </Box>

      {/* Hero 2 – Downstream consumers */}
      <Box
        className={classes.hero}
        style={{
          backgroundImage: "url('/docs-content/img/landing/DD_downstream_consumers.png')",
          marginTop: 40,
        }}
      >
        <Box className={classes.heroOverlay} />
        <Box className={classes.heroInner}>
          <img
            src="/docs-content/img/landing/DD_triangle.png"
            alt="deepc clinical"
            className={classes.heroImg}
          />
          <Box className={classes.heroLeft}>
            <Typography component="h1" className={classes.heroTitle}>
              For downstream consumers of AI results
            </Typography>
            <Typography component="p" className={classes.heroSubtitle}>
              Consume structured AI results and integrate them into your systems for an enhanced
              clinical workflow. Leverage any API standard — webhooks, GraphQL, FHIR, and more.
            </Typography>
            <button
              className={classes.heroBtn}
              onClick={() => onNavigate('clinical-overview')}
            >
              Go to Clinical API →
            </button>
          </Box>
        </Box>
      </Box>
      <Box className={classes.quickLinks}>
        <Typography className={classes.quickLinksTitle}>Quick Links</Typography>
        <Typography className={classes.quickLinksSubtitle}>
          Jump-start your clinical integration with these helpful shortcuts
        </Typography>
        <Box className={classes.quickLinkTags}>
          {clinicalQuickLinks.map(link => (
            <button
              key={link.docId}
              className={classes.quickLinkTag}
              onClick={() => onNavigate(link.docId)}
            >
              {link.label}
            </button>
          ))}
        </Box>
      </Box>

      <Box className={classes.homeFooter} />
    </Box>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────

interface NavItemProps {
  item: DocItem;
  depth: number;
  selectedId: string;
  openIds: Set<string>;
  onSelect: (item: DocItem) => void;
  onToggle: (id: string) => void;
}

function NavItem({ item, depth, selectedId, openIds, onSelect, onToggle }: NavItemProps) {
  const { classes } = useStyles();
  const isOpen = openIds.has(item.id);
  const isActive = item.id === selectedId;

  const indentClass =
    depth === 1 ? classes.navItemIndent1
      : depth === 2 ? classes.navItemIndent2
        : depth === 3 ? classes.navItemIndent3
          : classes.navItemIndent4;

  if (item.children?.length) {
    // A category that also has its own page navigates on row-click and toggles
    // its children; the chevron also toggles them. Pure categories (no path)
    // toggle on row-click.
    const navigates = Boolean(item.path);
    return (
      <>
        <ListItem
          button
          className={`${classes.navItem} ${navigates && isActive ? classes.navItemActive : ''} ${indentClass}`}
          onClick={() => {
            if (navigates) onSelect(item);
            onToggle(item.id);
          }}
          disableGutters
        >
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{ className: classes.navItemCategory }}
          />
          <Box
            component="span"
            display="flex"
            onClick={e => { e.stopPropagation(); onToggle(item.id); }}
          >
            {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children.map(child => (
              <NavItem
                key={child.id}
                item={child}
                depth={depth + 1}
                selectedId={selectedId}
                openIds={openIds}
                onSelect={onSelect}
                onToggle={onToggle}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItem
      button
      className={`${classes.navItem} ${isActive ? classes.navItemActive : ''} ${indentClass}`}
      onClick={() => onSelect(item)}
      disableGutters
    >
      <ListItemText
        primary={item.title}
        primaryTypographyProps={{ className: classes.navItemText }}
      />
    </ListItem>
  );
}


function DocsSidebar({
  selectedId, openIds, onSelect, onToggle,
}: {
  selectedId: string;
  openIds: Set<string>;
  onSelect: (item: DocItem) => void;
  onToggle: (id: string) => void;
}) {
  const { classes } = useStyles();
  const sections = docsStructure.filter(i => i.id !== HOME_ID);

  return (
    <Box className={classes.sidebar}>
      {/* Doc sections */}
      {sections.map((section, idx) => (
        <Box key={section.id}>
          {idx > 0 && <Divider style={{ margin: '12px 0' }} />}
          <Typography className={classes.sectionLabel}>{section.title}</Typography>
          <List disablePadding>
            {(section.children ?? []).map(item => (
              <NavItem
                key={item.id}
                item={item}
                depth={1}
                selectedId={selectedId}
                openIds={openIds}
                onSelect={onSelect}
                onToggle={onToggle}
              />
            ))}
          </List>
        </Box>
      ))}

    </Box>
  );
}

// ── Doc content area ──────────────────────────────────────────────────────

function DocsContentArea({
  docItem,
  onNavigate,
  onNavigateByPath,
  hash,
}: {
  docItem: DocItem | undefined;
  onNavigate: (id: string) => void;
  onNavigateByPath: (urlPath: string, hash?: string) => void;
  hash?: string;
}) {
  const { classes } = useStyles();
  const components = makeComponents(classes, onNavigateByPath, docItem?.path ?? '');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Scroll back to top on page navigation (skip when navigating to a hash target).
  useEffect(() => {
    if (hash) return;
    if (!wrapperRef.current) return;
    let el: Element | null = wrapperRef.current.parentElement;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo(0, 0);
  }, [docItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // After content renders, scroll to the anchor.
  // 1. Try direct id match (covers <a name> transferred to heading by remarkNamedAnchors).
  // 2. Fall back to slug-matching heading textContent — same algorithm medusa/Docusaurus
  //    uses (github-slugger), so cross-page links generated by medusa always resolve.
  useEffect(() => {
    if (!hash || segments.length === 0) return;
    const id = hash.replace(/^#/, '');
    const slugify = (s: string) =>
      s.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
    const timer = setTimeout(() => {
      let target: Element | null = document.getElementById(id);
      if (!target) {
        const headings = document.querySelectorAll('h1,h2,h3,h4,h5,h6');
        for (const h of Array.from(headings)) {
          if (slugify(h.textContent ?? '') === id) { target = h; break; }
        }
      }
      if (!target) return;
      // Use the same scroll-container logic as the scroll-to-top effect so we
      // always scroll the right element (avoids scrollIntoView quirks with
      // Backstage's nested overflow containers).
      let container: Element | null = wrapperRef.current?.parentElement ?? null;
      while (container) {
        const { overflowY } = window.getComputedStyle(container);
        if (overflowY === 'auto' || overflowY === 'scroll') break;
        container = container.parentElement;
      }
      if (container) {
        const targetTop = (target as HTMLElement).getBoundingClientRect().top;
        // The page itself scrolls: its rect moves with the scroll.
        const containerTop =
          container === document.documentElement
            ? 0
            : container.getBoundingClientRect().top;
        container.scrollTop += targetTop - containerTop - 16;
      } else {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [segments, hash]);

  // Programmatic download for links injected as raw HTML inside <details> blocks —
  // the SPA dev server intercepts direct navigation.
  const handleContainerClick = React.useCallback(async (e: React.MouseEvent) => {
    const anchor = (e.target as Element).closest('a.docs-file-dl') as HTMLAnchorElement | null;
    if (!anchor) return;
    e.preventDefault();
    const href = anchor.getAttribute('href') ?? '';
    const filename = href.split('/').pop() ?? 'download';
    try {
      const res = await fetch(href);
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(href, '_blank');
    }
  }, []);

  useEffect(() => {
    setSegments([]);
    if (!docItem?.path) return;
    setLoading(true);
    setError(null);

    fetch(`/docs-content/${docItem.path}`)
      .then(res => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.text();
      })
      .then(async raw => {
        const docDir = (docItem.path ?? '').split('/').slice(0, -1).join('/');
        const rawLoaderVarMap = extractRawLoaderVars(raw, docDir);

        // Fetch all raw-loader referenced files in parallel.
        const varContents: Record<string, string> = {};
        await Promise.all(
          Object.entries(rawLoaderVarMap).map(async ([varName, filePath]) => {
            try {
              const r = await fetch(`/docs-content/${filePath}`);
              if (r.ok) varContents[varName] = await r.text();
            } catch {
              // leave the {VarName} placeholder in place
            }
          }),
        );

        const processed = preprocessContent(raw, docItem.path, varContents);
        setSegments(parseSegments(processed));
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [docItem]);

  // Homepage
  if (!docItem || docItem.id === HOME_ID) {
    return <DocsHomePage onNavigate={onNavigate} />;
  }

  if (loading) {
    return (
      <Box className={classes.loadingBox}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorBox}>
        <Typography variant="body2">Failed to load: {error}</Typography>
      </Box>
    );
  }

  return (
    <div ref={wrapperRef} onClick={handleContainerClick}>
      {renderSegments(segments, components)}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function DocsV2Page() {
  const { classes } = useStyles();
  const { '*': urlPath = '' } = useParams<{ '*': string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive the selected doc from the URL path
  const selectedDoc = urlPath ? findDocByUrlPath(docsStructure, urlPath) : undefined;
  const selectedId = selectedDoc?.id ?? HOME_ID;
  const isHome = !urlPath;

  // Keep ancestor sections open whenever the active doc changes
  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    const ancestors = selectedDoc
      ? getAncestorIds(docsStructure, selectedDoc.id) ?? []
      : [];
    return new Set(ancestors);
  });

  useEffect(() => {
    if (selectedDoc) {
      const ancestors = getAncestorIds(docsStructure, selectedDoc.id) ?? [];
      setOpenIds(prev => new Set([...prev, ...ancestors]));
    }
  }, [urlPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback(
    (item: DocItem) => {
      if (item.id === HOME_ID || !item.path) {
        navigate('/docs-v2');
      } else {
        navigate(`/docs-v2/${docPathToUrl(item.path)}`);
      }
    },
    [navigate],
  );

  const handleNavigate = useCallback(
    (id: string) => {
      const item = findDocById(docsStructure, id);
      if (item) handleSelect(item);
    },
    [handleSelect],
  );

  const handleNavigateByPath = useCallback(
    (urlPath: string, hash?: string) => {
      navigate(`/docs-v2/${urlPath}${hash ?? ''}`);
    },
    [navigate],
  );

  const handleToggle = useCallback((id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <Page themeId="tool">
      <Header title="Developer Docs">
        {/* The docs are still being written: say so on every page. */}
        <Chip
          label="Work in progress"
          size="small"
          variant="outlined"
          style={{ borderColor: '#fff', color: '#fff' }}
        />
      </Header>
      <Content noPadding className={classes.content}>
        <Box className={classes.layout}>
          <DocsSidebar
            selectedId={selectedId}
            openIds={openIds}
            onSelect={handleSelect}
            onToggle={handleToggle}
          />
          <Box className={classes.rightColumn}>
            {isHome ? (
              <Box style={{ flexGrow: 1 }}>
                <DocsHomePage onNavigate={handleNavigate} />
              </Box>
            ) : (
              <Box className={classes.contentArea}>
                <DocsContentArea docItem={selectedDoc} onNavigate={handleNavigate} onNavigateByPath={handleNavigateByPath} hash={location.hash} />
              </Box>
            )}
          </Box>
        </Box>
      </Content>
      <DocsAssistantWidget />
    </Page>
  );
}
